/**
 * Clinical AI Agent - Tools Tab (Comfort Kit, Medical Export)
 */

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Package, Download, Loader2, CheckCircle, AlertTriangle, Pill } from "lucide-react";
import { toast } from "sonner";
import { COMFORT_KIT_TYPES, CAREGIVER_COMFORT_LEVELS, API_URL } from "./constants";

export default function ToolsTab({
  patientId,
  loading,
  setLoading,
  isDark,
  token
}) {
  const [comfortKitForm, setComfortKitForm] = useState({
    kit_type: "standard",
    current_symptoms: [],
    caregiver_comfort_level: "comfortable"
  });
  const [comfortKitResult, setComfortKitResult] = useState(null);
  const [exportFormat, setExportFormat] = useState("summary");
  const [exportResult, setExportResult] = useState(null);

  // Generate comfort kit
  const generateComfortKit = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-ai-agent/comfort-kit/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patientId,
          ...comfortKitForm
        })
      });

      if (res.ok) {
        const data = await res.json();
        setComfortKitResult(data);
        toast.success("Comfort kit recommendations generated");
      } else {
        toast.error("Failed to generate comfort kit");
      }
    } catch (error) {
      toast.error("Error generating comfort kit");
    } finally {
      setLoading(false);
    }
  };

  // Export medical record
  const exportMedicalRecord = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${API_URL}/api/clinical-ai-agent/medical-record/export/${patientId}?format=${exportFormat}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setExportResult(data);
        toast.success("Medical record export complete");
      } else {
        toast.error("Export failed");
      }
    } catch (error) {
      toast.error("Error exporting medical record");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Comfort Kit Section */}
      <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Package className="h-4 w-4 text-green-400" />
            Comfort Kit Generator
          </CardTitle>
          <CardDescription className="text-xs">
            Generate medication recommendations and caregiver education
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs">Kit Type</Label>
              <Select
                value={comfortKitForm.kit_type}
                onValueChange={(v) => setComfortKitForm({ ...comfortKitForm, kit_type: v })}
              >
                <SelectTrigger className={isDark ? "bg-slate-700 border-slate-600" : ""}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMFORT_KIT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Caregiver Comfort Level</Label>
              <Select
                value={comfortKitForm.caregiver_comfort_level}
                onValueChange={(v) => setComfortKitForm({ ...comfortKitForm, caregiver_comfort_level: v })}
              >
                <SelectTrigger className={isDark ? "bg-slate-700 border-slate-600" : ""}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAREGIVER_COMFORT_LEVELS.map((l) => (
                    <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            onClick={generateComfortKit}
            disabled={loading}
            className="w-full bg-gradient-to-r from-green-600 to-teal-600"
            data-testid="generate-comfort-kit-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Package className="h-4 w-4 mr-2" />}
            Generate Comfort Kit
          </Button>
        </CardContent>
      </Card>

      {/* Comfort Kit Result */}
      {comfortKitResult && (
        <Card className={`${isDark ? "bg-slate-800/80 border-green-500/30" : "bg-green-50 border-green-200"}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-400" />
              Comfort Kit Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="max-h-80">
              <div className="space-y-4">
                {/* Medications */}
                {comfortKitResult.recommended_medications && (
                  <div>
                    <p className="text-xs font-semibold mb-2 flex items-center gap-1">
                      <Pill className="h-3 w-3" />
                      Recommended Medications
                    </p>
                    <div className="space-y-2">
                      {comfortKitResult.recommended_medications.map((med, i) => (
                        <div 
                          key={i}
                          className={`p-2 rounded ${isDark ? "bg-slate-700" : "bg-white"}`}
                        >
                          <p className="text-sm font-medium">{med.medication}</p>
                          <p className="text-xs text-gray-500">
                            {med.indication} | {med.dose} {med.route} {med.frequency}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Caregiver Instructions */}
                {comfortKitResult.caregiver_instructions && comfortKitResult.caregiver_instructions.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold mb-1">Caregiver Instructions:</p>
                    <ul className="text-xs space-y-1">
                      {comfortKitResult.caregiver_instructions.map((inst, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-green-400">•</span>
                          {inst}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Safety Guidelines */}
                {comfortKitResult.safety_guidelines && comfortKitResult.safety_guidelines.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold mb-1">Safety Guidelines:</p>
                    <ul className="text-xs space-y-1">
                      {comfortKitResult.safety_guidelines.map((guide, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-yellow-400">⚠</span>
                          {guide}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* When to Call Hospice */}
                {comfortKitResult.when_to_call_hospice && comfortKitResult.when_to_call_hospice.length > 0 && (
                  <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
                    <p className="text-xs font-semibold mb-1 text-red-400">When to Call Hospice:</p>
                    <ul className="text-xs space-y-1">
                      {comfortKitResult.when_to_call_hospice.map((when, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <AlertTriangle className="h-3 w-3 text-red-400 mt-0.5" />
                          {when}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Documentation Template */}
                {comfortKitResult.documentation_template && (
                  <div className="p-2 rounded bg-gray-100 dark:bg-slate-700">
                    <p className="text-xs font-semibold mb-1">Documentation:</p>
                    <p className="text-xs italic">{comfortKitResult.documentation_template}</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Medical Record Export */}
      <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Download className="h-4 w-4 text-blue-400" />
            Medical Record Export
          </CardTitle>
          <CardDescription className="text-xs">
            Export patient data for surveys, transfers, or audits
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs">Export Format</Label>
            <Select
              value={exportFormat}
              onValueChange={setExportFormat}
            >
              <SelectTrigger className={isDark ? "bg-slate-700 border-slate-600" : ""}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="summary">Summary (Overview)</SelectItem>
                <SelectItem value="survey_ready">Survey Ready (Compliance)</SelectItem>
                <SelectItem value="full">Full Record</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={exportMedicalRecord}
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600"
            data-testid="export-medical-record-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Export Medical Record
          </Button>
        </CardContent>
      </Card>

      {/* Export Result */}
      {exportResult && (
        <Card className={`${isDark ? "bg-slate-800/80 border-blue-500/30" : "bg-blue-50 border-blue-200"}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-400" />
              Export Complete
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline">Format: {exportResult.format}</Badge>
                <Badge variant="outline">{exportResult.total_records} records</Badge>
              </div>
              <p className="text-xs text-gray-500">
                Export ID: {exportResult.export_id}
              </p>
              <p className="text-xs text-gray-500">
                Sections: {exportResult.sections_included?.join(", ")}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
